import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useEffect } from 'react';
import Hls from 'hls.js';
import { VideoFeatures } from '@/store/videoFeatures';
import { StoryboardPreview } from './StoryboardPreview';

interface VideoElementProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  features: VideoFeatures;
  togglePlay: () => void;
  useCustomControls: boolean;
  isMuted: boolean;
  showAutoplayOverlay: boolean;
  autoplayOverlayEnabled: boolean;
  onTimeUpdate: () => void;
  onDurationChange: () => void;
  onPlay: () => void;
  onPause: () => void;
  onVolumeChange: () => void;
  onEnded?: () => void;
  onAutoplayOverlayClick?: () => void;
  /** Altura máxima do vídeo (ex.: '500px'). Contém VSLs verticais no VSLeads,
   *  que senão esticam o player. Mantém o aspect ratio e centraliza. */
  maxHeight?: string;
}

export const VideoElement: React.FC<VideoElementProps> = ({
  videoRef,
  features,
  togglePlay,
  useCustomControls,
  isMuted,
  showAutoplayOverlay,
  autoplayOverlayEnabled,
  onTimeUpdate,
  onDurationChange,
  onPlay,
  onPause,
  onVolumeChange,
  onEnded,
  onAutoplayOverlayClick,
  maxHeight
}) => {
  useLanguage();
  // Get poster image from either customThumbnail or initialThumbnail
  const posterImage = features.customThumbnail || features.initialThumbnail?.imageUrl || undefined;

  // Pick the best playback URL: prefer HLS (videoStreamUrl) for fast start.
  // Falls back to the MP4 (videoUrl) if no HLS playlist is available.
  const streamUrl = features.videoStreamUrl;
  const fallbackUrl = features.videoUrl;
  const playbackUrl = streamUrl || fallbackUrl;
  const isHls = !!playbackUrl && /\.m3u8(\?|$)/i.test(playbackUrl);

  // Attach hls.js when the URL is an HLS playlist and the browser doesn't support
  // it natively (Chrome/Firefox/Edge). Safari (iOS/macOS) plays HLS natively via
  // the <source> below, so we skip hls.js there.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isHls || !playbackUrl) return;

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari path: native HLS — the <source> element handles it.
      return;
    }

    if (!Hls.isSupported()) {
      console.warn('HLS not supported in this browser; cannot play stream URL');
      return;
    }

    const hls = new Hls({ enableWorker: true });
    hls.loadSource(playbackUrl);
    hls.attachMedia(video);
    // Exposto pra handleTvCast trocar pro src direto antes de remote.prompt()
    // (a Remote Playback API não consegue transmitir conteúdo MSE).
    (video as HTMLVideoElement & { _hlsInstance?: Hls })._hlsInstance = hls;

    return () => {
      try { delete (video as HTMLVideoElement & { _hlsInstance?: Hls })._hlsInstance; } catch {}
      hls.destroy();
    };
  }, [isHls, playbackUrl, videoRef]);

  // Handle video click - if autoplay overlay is active, use autoplay click handler
  // Allow toggle play if:
  // 1. bigPlayButton is enabled (from controls config)
  // 2. OR autoplay overlay is not currently showing
  const handleVideoClick = () => {
    if (showAutoplayOverlay && onAutoplayOverlayClick) {
      onAutoplayOverlayClick();
    } else if (!features.disableControls && (features.bigPlayButton || !showAutoplayOverlay)) {
      togglePlay();
    }
  };

  return (
    <>
      <video
        key={features.videoId || 'default'}
        ref={videoRef}
        className="block"
        style={maxHeight
          ? { width: 'auto', height: 'auto', maxWidth: '100%', maxHeight, margin: '0 auto' }
          : features.largePlayer
            ? { width: '100%', height: 'auto' }
            : { width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '82vh' }
        }
        controls={
          features.disableControls
            || (features.playerThemeEnabled && features.controlBarEnabled === false)
            ? false
            : (features.showControls && !useCustomControls && !showAutoplayOverlay)
        }
        poster={posterImage}
        autoPlay={features.autoPlay}
        loop={features.loop}
        playsInline
        onClick={handleVideoClick}
        muted={autoplayOverlayEnabled && showAutoplayOverlay ? true : isMuted}
        onTimeUpdate={onTimeUpdate}
        onDurationChange={onDurationChange}
        onPlay={onPlay}
        onPause={onPause}
        onVolumeChange={onVolumeChange}
        onEnded={onEnded}
        {...(features.tvCastEnabled
          ? ({ 'x-webkit-airplay': 'allow' } as Record<string, string>)
          : {})}
      >
        {/* For MP4: <source> handles loading natively.
            For HLS on Safari: <source> with m3u8 plays natively.
            For HLS on Chrome/Firefox/Edge: hls.js attaches via the useEffect above
            (the <source> tag is harmless in that case — the browser ignores it). */}
        {playbackUrl && (
          <source
            src={playbackUrl}
            type={isHls ? 'application/vnd.apple.mpegurl' : 'video/mp4'}
          />
        )}
        {translateInterfaceText("Your browser does not support the video tag.")}</video>
      <StoryboardPreview
        videoRef={videoRef}
        storyboardUrl={features.storyboardUrl}
        // Storyboard depende de controles reais — fake bar não é compatível.
        enabled={features.storyboardEnabled && features.showControls}
      />
    </>
  );
};
